import { Check, Copy } from "lucide-react";
import { useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import AILogoEditor from "@/components/ai/AILogoEditor";

// Damier affiché derrière un logo détouré : sans lui, un fond transparent
// se confond avec le blanc de la carte et l'option ne se voit pas.
const CHECKERBOARD = {
  backgroundImage: "linear-gradient(45deg,#e5e5e5 25%,transparent 25%),linear-gradient(-45deg,#e5e5e5 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#e5e5e5 75%),linear-gradient(-45deg,transparent 75%,#e5e5e5 75%)",
  backgroundSize: "12px 12px",
  backgroundPosition: "0 0,0 6px,6px -6px,-6px 0",
};

/**
 * Rendu d'un résultat IA selon son TYPE.
 *
 * Le module ne suppose jamais qu'une réponse est du texte (§10 du cahier des
 * charges) : chaque type a sa présentation, et chacun reste modifiable avant
 * application quand ça a du sens. Un type inconnu — parce que le serveur en
 * a introduit un nouveau avant que le frontend ne soit redéployé — s'affiche
 * en JSON brut plutôt que de laisser un écran vide.
 */
const Lbl = ({ children }) => (
  <label className="text-[11px] uppercase tracking-[0.15em] text-ash">{children}</label>
);

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <button
      type="button"
      data-testid="ai-result-copy"
      onClick={() => {
        navigator.clipboard?.writeText(value)
          .then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); })
          .catch(() => toast.error("Copie impossible"));
      }}
      className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-ash hover:text-ink"
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? "Copié" : "Copier"}
    </button>
  );
}

/** Champs d'un résultat JSON, avec compteur quand une longueur est conseillée. */
const JSON_FIELD_LABELS = {
  title: { label: "Titre", max: 60 },
  description: { label: "Description", max: 160 },
  keywords: { label: "Mots-clés", max: null },
};

// `onEditLogo(instruction, options)` / `editingLogo` / `logoOptions` :
// retouche d'un logo (modale de l'assistant). Absents : pas de retouche.
export default function AIResult({ result, onChange, onEditLogo, editingLogo = false, logoOptions }) {
  if (!result) return null;

  // ---- Texte -------------------------------------------------------------
  if (result.type === "text" || result.type === "rich_text") {
    return (
      <div className="space-y-2" data-testid="ai-result-text">
        <div className="flex items-center justify-between">
          <Lbl>Proposition</Lbl>
          <CopyButton value={result.content} />
        </div>
        <Textarea
          data-testid="ai-result-textarea"
          value={result.content || ""}
          onChange={(e) => onChange({ ...result, content: e.target.value })}
          rows={7}
          className="rounded-sm border-stone bg-white text-base md:text-sm"
        />
      </div>
    );
  }

  // ---- Suggestions -------------------------------------------------------
  if (result.type === "suggestions") {
    const items = result.items || [];
    const chosen = result.chosen ?? null;
    return (
      <div className="space-y-2" data-testid="ai-result-suggestions">
        <Lbl>Choisissez une proposition</Lbl>
        <ul className="space-y-1.5">
          {items.map((item, i) => (
            <li key={i}>
              <button
                type="button"
                data-testid={`ai-suggestion-${i}`}
                onClick={() => onChange({ ...result, chosen: i, content: item })}
                aria-pressed={chosen === i}
                className={`w-full text-left text-sm px-3 py-2 rounded-sm border transition-colors ${
                  chosen === i
                    ? "border-copper bg-copper/10 text-ink"
                    : "border-stone bg-white text-graphite hover:border-copper/50 hover:text-ink"
                }`}
              >
                {item}
              </button>
            </li>
          ))}
        </ul>
        {chosen === null && (
          <p className="text-[11px] text-ash">Sélectionnez-en une pour pouvoir l&apos;appliquer.</p>
        )}
      </div>
    );
  }

  // ---- JSON à clés nommées (balises SEO…) --------------------------------
  if (result.type === "json") {
    const data = result.data || {};
    return (
      <div className="space-y-3" data-testid="ai-result-json">
        {Object.entries(data).map(([key, value]) => {
          const meta = JSON_FIELD_LABELS[key] || { label: key, max: null };
          const text = typeof value === "string" ? value : JSON.stringify(value);
          const tooLong = meta.max && text.length > meta.max;
          return (
            <div key={key}>
              <div className="flex items-center justify-between gap-2">
                <Lbl>{meta.label}</Lbl>
                {meta.max && (
                  <span className={`text-[11px] tabular-nums ${tooLong ? "text-red-600" : "text-ash"}`}>
                    {text.length}/{meta.max}
                  </span>
                )}
              </div>
              <Textarea
                data-testid={`ai-result-field-${key}`}
                value={text}
                onChange={(e) => onChange({ ...result, data: { ...data, [key]: e.target.value } })}
                rows={key === "description" ? 3 : 2}
                className="mt-1 rounded-sm border-stone bg-white text-base md:text-sm"
              />
            </div>
          );
        })}
      </div>
    );
  }

  // ---- Palette / thème ---------------------------------------------------
  if (result.type === "color_palette" || result.type === "theme") {
    const palette = result.palette || {};
    return (
      <div className="space-y-3" data-testid="ai-result-palette">
        <Lbl>Palette proposée</Lbl>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {Object.entries(palette).map(([name, hex]) => (
            <div key={name} className="flex items-center gap-2 border border-stone rounded-sm p-2 bg-white">
              <span
                className="h-7 w-7 rounded-sm border border-stone shrink-0"
                style={{ backgroundColor: hex }}
                aria-hidden="true"
              />
              <span className="min-w-0">
                <span className="block text-xs text-ink truncate">{name}</span>
                <span className="block text-[11px] text-ash font-mono">{hex}</span>
              </span>
            </div>
          ))}
        </div>
        {result.font_pair && (
          <p className="text-xs text-graphite">
            Typographie proposée : <span className="font-medium text-ink">{result.font_pair_label || result.font_pair}</span>
          </p>
        )}
        {/* Forme et espacement peuvent manquer d'une réponse produite à
            partir d'un prompt antérieur à leur introduction : on ne les
            affiche que présents, plutôt qu'une ligne « — » qui laisserait
            croire que le modèle a choisi « rien ». */}
        {result.shape && (
          <p className="text-xs text-graphite">
            Forme proposée :{" "}
            <span className="font-medium text-ink">
              {result.shape_labels?.radius || result.shape.radius}
              {" · ombre "}
              {(result.shape_labels?.shadow || result.shape.shadow).toLowerCase()}
            </span>
          </p>
        )}
        {result.spacing && (
          <p className="text-xs text-graphite">
            Espacement proposé :{" "}
            <span className="font-medium text-ink">{result.spacing_label || result.spacing.density}</span>
          </p>
        )}
        {/* Structure proposée — l'ordre est l'information : c'est le parcours
            de lecture de la page, de haut en bas. */}
        {!!(result.section_labels || []).length && (
          <div data-testid="ai-result-composition">
            <Lbl>Structure de la page</Lbl>
            <ol className="mt-1.5 border border-stone rounded-sm divide-y divide-stone bg-white">
              {result.section_labels.map((label, i) => (
                <li key={`${label}-${i}`} className="flex items-center gap-2 px-2.5 py-1.5">
                  <span className="text-[10px] tabular-nums text-ash w-4 shrink-0">{i + 1}</span>
                  <span className="text-xs text-ink truncate">{label}</span>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Textes rédigés. On en montre un échantillon plutôt que la liste
            entière : l'aperçu réel, c'est le Canvas une fois appliqué. */}
        {!!(result.content || []).length && (
          <div data-testid="ai-result-content">
            <Lbl>Textes rédigés ({result.content.length})</Lbl>
            <ul className="mt-1.5 border border-stone rounded-sm divide-y divide-stone bg-white">
              {result.content.slice(0, 6).map((entry, i) => (
                <li key={`${entry.section}-${entry.block}-${entry.field}-${i}`} className="px-2.5 py-1.5">
                  <span className="block text-[10px] uppercase tracking-[0.1em] text-ash">
                    {entry.section}
                    {entry.block ? ` · bloc ${entry.block}` : ""} · {entry.field}
                  </span>
                  <span className="block text-xs text-ink line-clamp-2">{entry.value}</span>
                </li>
              ))}
            </ul>
            {result.content.length > 6 && (
              <p className="text-[11px] text-ash mt-1">
                et {result.content.length - 6} autres — visibles dans l&apos;aperçu.
              </p>
            )}
          </div>
        )}

        {/* Ce que l'assistant aurait voulu et qui n'existe pas. Volontairement
            présenté comme une piste de développement, jamais comme quelque
            chose qui va s'appliquer : rien de tout ça n'est appliqué. */}
        {!!(result.missing_sections || []).length && (
          <div data-testid="ai-result-missing">
            <Lbl>Sections souhaitées mais absentes du système</Lbl>
            <ul className="mt-1.5 flex flex-wrap gap-1.5">
              {result.missing_sections.map((name) => (
                <li
                  key={name}
                  className="text-[11px] px-2 py-1 rounded-sm border border-dashed border-copper/60 bg-copper/5 text-graphite"
                >
                  {name}
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-ash mt-1">
              Non appliquées — à développer si elles vous semblent utiles.
            </p>
          </div>
        )}

        {result.rationale && <p className="text-xs text-graphite italic">{result.rationale}</p>}
      </div>
    );
  }

  // ---- Images ------------------------------------------------------------
  if (result.type === "logo") {
    const images = result.images || [];
    // Une seule image : elle est retenue d'office, inutile de faire cliquer.
    const chosen = result.chosen ?? (images.length === 1 ? 0 : null);
    const versionOf = (path) => images.findIndex((im) => im.path === path) + 1;
    const hasVersions = images.some((im) => im.parent_path);
    return (
      <div className="space-y-2" data-testid="ai-result-logo">
        <Lbl>{hasVersions ? "Versions — choisissez celle à garder" : images.length > 1 ? "Choisissez une proposition" : "Proposition"}</Lbl>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {images.map((img, i) => (
            <button
              key={img.path || i}
              type="button"
              data-testid={`ai-image-${i}`}
              onClick={() => onChange({ ...result, chosen: i, url: img.url })}
              aria-pressed={chosen === i}
              disabled={editingLogo}
              className={`border rounded-sm p-2 bg-white text-left transition-colors disabled:cursor-wait ${
                chosen === i ? "border-copper ring-1 ring-copper/40" : "border-stone hover:border-copper/50"
              }`}
            >
              <div className="rounded-sm" style={img.transparent ? CHECKERBOARD : undefined}>
                <img
                  src={img.url}
                  alt={`Version ${i + 1}`}
                  loading="lazy"
                  className="w-full h-28 object-contain"
                />
              </div>
              <span className="flex items-center justify-between gap-2 mt-1">
                <span className="text-[11px] text-ink font-medium">Version {i + 1}</span>
                {!!img.width && (
                  <span className="text-[10px] text-ash tabular-nums">{img.width}×{img.height}</span>
                )}
              </span>
              {img.parent_path && (
                <span className="block text-[10px] text-graphite line-clamp-2 mt-0.5" data-testid={`ai-image-${i}-origin`}>
                  {`Retouche de la version ${versionOf(img.parent_path) || "?"}`}
                  {img.instruction ? ` : « ${img.instruction} »` : ""}
                </span>
              )}
              {img.transparent && <span className="block text-[10px] text-copper mt-0.5">Fond transparent</span>}
            </button>
          ))}
          {editingLogo && (
            <div className="border border-dashed border-copper/60 rounded-sm p-2 flex flex-col items-center justify-center gap-2 text-[11px] text-graphite min-h-[9rem]" data-testid="ai-logo-edit-pending">
              <span className="h-5 w-5 rounded-full border-2 border-copper border-t-transparent animate-spin" aria-hidden="true" />
              Nouvelle version en cours…
            </div>
          )}
        </div>
        {!!(result.notices || []).length && (
          <ul className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 space-y-1" data-testid="ai-logo-notices">
            {result.notices.map((n) => <li key={n}>{n}</li>)}
          </ul>
        )}
        {onEditLogo && chosen !== null && images[chosen]?.path && !(result.variants || []).length && (
          <AILogoEditor
            versionLabel={`Version ${chosen + 1}`}
            options={logoOptions}
            busy={editingLogo}
            onEdit={onEditLogo}
          />
        )}
        {!!(result.variants || []).length && (
          <div className="pt-1">
            <Lbl>Déclinaisons prêtes à l&apos;emploi</Lbl>
            <p className="text-[11px] text-ash mt-0.5 mb-2">
              Toutes déposées dans la médiathèque. Rien à redimensionner à la main.
            </p>
            <ul className="border border-stone rounded-sm divide-y divide-stone bg-white">
              {result.variants.map((v) => (
                <li key={v.key} className="flex items-center gap-3 p-2">
                  <img
                    src={v.url}
                    alt=""
                    loading="lazy"
                    className="h-8 w-8 object-contain shrink-0 border border-stone rounded-sm bg-sand"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-xs text-ink truncate">{v.label}</span>
                    {/* À quoi sert la déclinaison : coupée sur une ligne en
                        320 px, l'information disparaissait au moment même où
                        elle est utile. */}
                    <span className="block text-[10px] text-ash line-clamp-2 sm:truncate">{v.purpose}</span>
                  </span>
                  <span className="text-[10px] text-ash tabular-nums shrink-0">
                    {v.width}×{v.height}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {!(result.variants || []).length && (
          <p className="text-[11px] text-graphite" data-testid="ai-logo-pending">
            Ceci est une proposition de style. Retouchez-la avec l&apos;IA si besoin (chaque retouche
            ajoute une version), puis « Valider ce style » produit le favicon, les icônes iOS/Android
            et les images de partage de la version choisie, et applique le tout. « Autre proposition »
            repart de zéro.
          </p>
        )}
        <p className="text-[11px] text-ash">
          Enregistrée dans la médiathèque — vous la retrouverez même sans l&apos;appliquer maintenant.
        </p>
      </div>
    );
  }

  return (
    <pre
      data-testid="ai-result-raw"
      className="text-xs bg-sand border border-stone rounded-sm p-3 overflow-x-auto"
    >
      {JSON.stringify(result, null, 2)}
    </pre>
  );
}
